import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toCssVariables, isRTL, DEFAULT_LANGUAGE } from 'ozira-app-core';

// Shared across every specialized product (spec: one design system, one RTL
// foundation, no per-product reimplementation). Originally apps/web-only;
// moved here so Code/Design/P/Forward get byte-identical theme/RTL wiring
// instead of five copies drifting apart.

type Mode = 'light' | 'dark';

interface ThemeState {
  mode: Mode;
  setMode: (mode: Mode) => void;
  language: string;
  setLanguage: (lang: string) => void;
  rtl: boolean;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

function savedMode(): Mode {
  try {
    return window.localStorage.getItem('ozira.appearance') === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeProvider({
  children,
  initialLanguage = DEFAULT_LANGUAGE,
}: {
  children: React.ReactNode;
  initialLanguage?: string;
}) {
  const [mode, updateMode] = useState<Mode>(savedMode);
  const setMode = (next: Mode) => {
    updateMode(next);
    // Storage restrictions must never prevent changing appearance in this tab.
    try { window.localStorage.setItem('ozira.appearance', next); } catch { /* session-only preference */ }
  };
  const [language, setLanguage] = useState<string>(initialLanguage);
  const rtl = useMemo(() => isRTL(language), [language]);

  // Design tokens come from the SHARED source of truth (packages/ozira-app-core),
  // applied as CSS custom properties -- this is the only place token VALUES are
  // consumed; component CSS only ever references var(--ozira-*).
  useEffect(() => {
    const variables = toCssVariables(mode);
    const root = document.documentElement;
    for (const [key, value] of Object.entries(variables)) root.style.setProperty(key, value);
    root.setAttribute('data-theme', mode);
    root.style.colorScheme = mode;
  }, [mode]);

  // RTL is architectural, not patched later: document direction follows the
  // selected language everywhere in one place -- logo never mirrors because
  // nothing here ever flips an image, only layout direction.
  useEffect(() => {
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', language);
  }, [rtl, language]);

  const value: ThemeState = { mode, setMode, language, setLanguage, rtl };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
